import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Mail, Trash2, Phone, Calendar, Inbox, FileText } from 'lucide-react';
import { contactApi } from '../../api/contact';

export function ContactManagement() {
    const { language } = useLanguage();
    const isRtl = language === 'ar';
    const L = (ar, en) => isRtl ? ar : en;

    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('contact'); 

    const fetchMessages = async () => {
        try {
            setLoading(true);
            const response = await contactApi.getMessages();
            const data = Array.isArray(response) ? response : (response.data || []);
            setMessages(data.reverse()); // الأحدث أولاً
        } catch (err) {
            console.error("Error fetching messages:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchMessages(); }, []);

    const handleDelete = async (id) => {
        if (!window.confirm(L('هل أنت متأكد من حذف هذه الرسالة؟', 'Delete this message?'))) return;
        try {
            await contactApi.deleteMessage(id);
            setMessages(prev => prev.filter(m => m.id !== id));
        } catch (err) {
            alert(L('فشل الحذف', 'Delete failed'));
        }
    };

    // فصل الرسائل بناءً على العنوان
    const contactMessages = messages.filter(m => !m.subject?.includes('Quote Request'));
    const quoteMessages = messages.filter(m => m.subject?.includes('Quote Request'));

    const displayMessages = activeTab === 'contact' ? contactMessages : quoteMessages;

    const tabStyle = (k) => ({
        padding: '14px 28px', border: 'none', cursor: 'pointer', fontWeight: 800, fontSize: 15,
        borderBottom: activeTab === k ? '3px solid #d4af37' : '3px solid transparent',
        color: activeTab === k ? '#d4af37' : '#64748b', background: 'none', transition: 'all 0.3s',
        display: 'flex', alignItems: 'center', gap: '8px'
    });

    return (
        <div className="admin-page-content" dir={isRtl ? 'rtl' : 'ltr'}>
            <header className="admin-page-header">
                <div>
                    <h1>{L('صندوق الوارد', 'Inbox')}</h1>
                    <p style={{color:'#64748b',fontSize:14}}>{L('إدارة رسائل العملاء وطلبات عروض الأسعار', 'Manage client messages and quote requests')}</p>
                </div>
            </header>

            <div style={{display:'flex', gap:16, borderBottom:'1px solid #e2e8f0', marginBottom:24, background:'white', borderRadius:'12px 12px 0 0', padding:'0 16px'}}>
                <button style={tabStyle('contact')} onClick={()=>setActiveTab('contact')}>
                    <Mail size={18} /> {L('تواصل معنا', 'Contact Us')} ({contactMessages.length})
                </button>
                <button style={tabStyle('quote')} onClick={()=>setActiveTab('quote')}>
                    <FileText size={18} /> {L('عروض الأسعار', 'Quote Requests')} ({quoteMessages.length})
                </button>
            </div>

            <div className="admin-content-card" style={{padding: '24px', background: 'transparent', boxShadow: 'none'}}>
                {loading ? <p style={{textAlign:'center', padding:40}}>{L('جاري التحميل...', 'Loading...')}</p> :
                displayMessages.length === 0 ? (
                    <div style={{textAlign:'center', padding:60, background: 'white', borderRadius: '16px'}}>
                        <Inbox size={48} color="#e2e8f0" style={{margin:'0 auto 16px'}}/>
                        <p style={{color:'#94a3b8', fontSize: '16px'}}>{L('لا توجد رسائل هنا حالياً.', 'No messages here yet.')}</p>
                    </div>
                ) : (
                    <div style={{display: 'grid', gap: '20px'}}>
                        {displayMessages.map(msg => (
                            <div key={msg.id} style={{background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 4px 15px rgba(0,0,0,0.03)', border: '1px solid #f1f5f9', position: 'relative'}}>
                                <button onClick={()=>handleDelete(msg.id)} style={{position: 'absolute', top: '24px', [isRtl?'left':'right']: '24px', background: '#fee2e2', color: '#ef4444', border: 'none', width: '36px', height: '36px', borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                                    <Trash2 size={18} />
                                </button>

                                <div style={{display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '15px'}}>
                                    <div style={{width: '50px', height: '50px', borderRadius: '50%', background: '#f8fafc', border: '1.5px solid #d4af37', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', fontWeight: 'bold', color: '#d4af37'}}>
                                        {(msg.name || 'U').charAt(0).toUpperCase()}
                                    </div>
                                    <div>
                                        <h3 style={{margin: 0, fontSize: '18px', color: '#0f172a'}}>{msg.name}</h3>
                                        <p style={{margin: 0, fontSize: '13px', color: '#64748b', display: 'flex', alignItems: 'center', gap: '5px', marginTop: '4px'}}>
                                            <Calendar size={13} /> {new Date(msg.createdAt || Date.now()).toLocaleDateString(isRtl ? 'ar-EG' : 'en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                        </p>
                                    </div>
                                </div>

                                <div style={{display: 'flex', gap: '20px', marginBottom: '20px', padding: '15px', background: '#f8fafc', borderRadius: '12px'}}>
                                    <div style={{display: 'flex', alignItems: 'center', gap: '8px', color: '#334155', fontSize: '14px', fontWeight: '600'}}>
                                        <Mail size={16} color="#d4af37" /> {msg.email}
                                    </div>
                                    <div style={{display: 'flex', alignItems: 'center', gap: '8px', color: '#334155', fontSize: '14px', fontWeight: '600'}}>
                                        <Phone size={16} color="#d4af37" /> <span style={{direction: 'ltr'}}>{msg.phone}</span>
                                    </div>
                                </div>

                                <div style={{padding: '20px', background: '#fffbfa', borderLeft: isRtl ? 'none' : '4px solid #d4af37', borderRight: isRtl ? '4px solid #d4af37' : 'none', borderRadius: '8px'}}>
                                    <h4 style={{margin: '0 0 10px 0', color: '#111', fontSize: '15px'}}>{msg.subject}</h4>
                                    <p style={{margin: 0, color: '#334155', lineHeight: '1.6', whiteSpace: 'pre-wrap', fontSize: '15px'}}>{msg.message}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
